import api from '@/services/apiService';
import { ClientMMRProfile } from '@/services/mmrService';

// ==============================================
// TYPE DEFINITIONS
// ==============================================

export interface SportCategoryStatItem {
  sport_category_id: number;
  sport_category_slug?: string;
  sport_category_name: string;
  sport_category_icon: string;
  games: number;
  total_match?: number;
  goals?: number;
  assists?: number;
  saves?: number;
  fouls?: number;
  clean_sheets?: number;
  wins?: number;
  win_rate?: number;
}

export interface UserStats {
  events?: number;
  total_events?: number;
  matches?: number;
  total_matches?: number;
  total_games?: number;
  win_rate?: number;
  sport_category_stats: SportCategoryStatItem[];
}

export interface UserCommunityItem {
  slug: string;
  name: string;
  logo: string;
  sportCategoryName: string;
  sportCategoryIcon: string;
  location: string;
  totalMembers: number;
}

export interface PublicUserProfile {
  id: number;
  name: string;
  username: string; // user-slug
  avatar?: string;
  profile_image?: string;
  location?: string;
  bio?: string; // description?
  birthdate?: string;
  profile_type?: 'invasion' | 'racket';
  information?: {
    position?: string;
    dominant_foot?: string;
    height?: number | string;
    location_code?: string;
    location_name?: string;
    gallery?: string[];
    [key: string]: any;
  };
  communities?: UserCommunityItem[];
  stats: UserStats;
  sport_stats?: Record<string, any>;
  mmr_profile?: ClientMMRProfile;
}

export interface GetPublicProfileResponse {
  success: boolean;
  message: string;
  data: PublicUserProfile;
}

class UserService {
  /**
   * Get public user profile by slug (username)
   * @param slug - User slug (username)
   * @returns User profile data
   */
  async getPublicProfile(slug: string): Promise<GetPublicProfileResponse> {
    const encodedSlug = encodeURIComponent(slug);
    const response = await api.get(`/public/users/${encodedSlug}`);
    return response.data;
  }

  /**
   * Get user's public events (history)
   * @param slug - User slug
   * @param type - Event type ('mabar' | 'sparing')
   * @param limit - Limit
   * @returns User events
   */
  async getUserEvents(
    slug: string,
    type: string = 'mabar',
    limit: number = 3,
    sportType?: string
  ): Promise<any> {
    const encodedSlug = encodeURIComponent(slug);
    const params: Record<string, any> = { type, limit };
    if (sportType) params.sport_type = sportType;
    const response = await api.get(`/public/users/${encodedSlug}/events`, {
      params,
    });
    return response.data;
  }

  /**
   * Get user's public communities
   * @param slug - User slug
   * @returns User communities
   */
  async getUserCommunities(slug: string): Promise<any> {
    const encodedSlug = encodeURIComponent(slug);
    const response = await api.get(`/public/users/${encodedSlug}/communities`);
    return response.data;
  }

  /**
   * Get user's public gallery
   * @param slug - User slug
   * @returns User gallery photos
   */
  async getUserGallery(slug: string): Promise<any> {
    const encodedSlug = encodeURIComponent(slug);
    const response = await api.get(`/public/users/${encodedSlug}/gallery`);
    return response.data;
  }
}

export default new UserService();
